<template>
    <main id="js-page-content" class="fts-v2" role="main">
        <portal to="ariane">
            <nav class="tbv2-breadcrumb" aria-label="Breadcrumb">
                <router-link to="/forms" class="tbv2-breadcrumb-link">{{ $t('forms.mine') }}</router-link>
                <span class="tbv2-breadcrumb-sep" aria-hidden="true">/</span>
                <span class="tbv2-breadcrumb-current">{{ $t('menu.forms.create') }}</span>
            </nav>
        </portal>

        <header class="fts-hero">
            <h1 class="fts-hero-title">{{ $t('menu.forms.create') }}</h1>
            <p class="fts-hero-sub">{{ $t('form_types.select_description') }}</p>
        </header>

        <div v-if="loading" class="fts-loading">
            <i class="fal fa-spinner-third fa-spin"></i>
            <span>{{ $t('forms.loading') }}…</span>
        </div>

        <section v-else>
            <div v-if="formTypes.length" class="fts-grid">
                <button
                    v-for="formType in formTypes"
                    :key="formType.id"
                    type="button"
                    class="fts-card"
                    :class="{ 'fts-card--selected': selected && selected.id === formType.id }"
                    @click="select(formType)"
                >
                    <span class="fts-card-icon" aria-hidden="true">
                        <i class="fal fa-layer-group"></i>
                    </span>
                    <span class="fts-card-body">
                        <span class="fts-card-title">{{ formType.name }}</span>
                        <span v-if="formType.description" class="fts-card-desc">{{ formType.description }}</span>
                        <span class="fts-card-meta">
                            <i class="fal fa-file-alt"></i>
                            {{ formType.forms_count || 0 }} {{ $t('form_types.forms_count').toLowerCase() }}
                        </span>
                    </span>
                    <span class="fts-card-check" aria-hidden="true">
                        <i class="fal fa-check"></i>
                    </span>
                </button>
            </div>

            <div v-else class="fts-empty">
                <div class="fts-empty-icon">
                    <i class="fal fa-layer-group"></i>
                </div>
                <p class="fts-empty-text">{{ $t('form_types.empty') }}</p>
            </div>

            <div v-if="selected" class="fts-footer">
                <button type="button" class="fts-next-btn" @click="proceed">
                    <span>{{ $t('forms.next') }}</span>
                    <i class="fal fa-arrow-right"></i>
                </button>
            </div>
        </section>
    </main>
</template>

<script>
import { mapActions } from 'vuex'

export default {
    name: 'FormTypeSelect',
    data() {
        return {
            formTypes: [],
            selected: null,
            loading: true
        }
    },
    created() {
        this.loadFormTypes()
    },
    methods: {
        ...mapActions('form', ['getFormTypes']),
        loadFormTypes() {
            this.loading = true
            this.getFormTypes()
                .then((response) => {
                    const types = response.data.types || response.data
                    this.formTypes = types.filter((t) => t.active)
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        select(formType) {
            this.selected = formType
        },
        proceed() {
            this.$router.push({
                name: 'CustomFormBuilder',
                query: { form_type_id: this.selected.id }
            })
        }
    }
}
</script>

<style scoped>
.fts-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-breadcrumb {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    font-weight: var(--v2-font-weight-medium);
}

.tbv2-breadcrumb-link {
    color: var(--v2-text-3);
    text-decoration: none;
    transition: color var(--v2-transition-fast);
}

.tbv2-breadcrumb-link:hover {
    color: var(--v2-primary);
}

.tbv2-breadcrumb-sep {
    color: var(--v2-text-4);
    font-weight: 400;
}

.tbv2-breadcrumb-current {
    color: var(--v2-text-1);
    font-weight: var(--v2-font-weight-semibold);
    letter-spacing: -0.005em;
}

.fts-hero {
    margin-bottom: var(--v2-space-5);
}

.fts-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.fts-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 640px;
}

.fts-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-2);
    padding: var(--v2-space-8, 48px) var(--v2-space-5);
    color: var(--v2-text-3);
    font-size: var(--v2-text-sm-size);
}

.fts-loading i {
    color: var(--v2-primary);
    font-size: 1rem;
}

.fts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--v2-space-3);
}

.fts-card {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: var(--v2-space-3);
    padding: var(--v2-space-4) var(--v2-space-5);
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    font: inherit;
    text-align: left;
    cursor: pointer;
    box-sizing: border-box;
    transition: border-color var(--v2-transition-fast),
        box-shadow var(--v2-transition-fast),
        transform var(--v2-transition-fast);
}

.fts-card:hover {
    border-color: var(--v2-primary);
    box-shadow: var(--v2-shadow-sm);
    transform: translateY(-1px);
}

.fts-card--selected {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.15);
}

.fts-card-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
}

.fts-card-icon i {
    color: inherit !important;
}

.fts-card-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.fts-card-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    line-height: 1.3;
}

.fts-card-desc {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.fts-card-meta {
    margin-top: 4px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.7rem;
    color: var(--v2-text-4);
    font-weight: var(--v2-font-weight-medium);
    letter-spacing: 0.01em;
}

.fts-card-meta i {
    font-size: 0.68rem;
}

.fts-card-check {
    position: absolute;
    top: var(--v2-space-3);
    right: var(--v2-space-3);
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--v2-primary);
    color: var(--v2-primary-contrast, #fff);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    opacity: 0;
    transform: scale(0.6);
    transition: opacity var(--v2-transition-fast), transform var(--v2-transition-fast);
}

.fts-card-check i {
    color: inherit !important;
}

.fts-card--selected .fts-card-check {
    opacity: 1;
    transform: scale(1);
}

.fts-empty {
    text-align: center;
    padding: 64px 24px;
    background: var(--v2-surface-0);
    border: 1px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
}

.fts-empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--v2-surface-2);
    color: var(--v2-text-4);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    margin-bottom: var(--v2-space-3);
}

.fts-empty-text {
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    margin: 0;
}

.fts-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: var(--v2-space-5);
}

.fts-next-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 20px;
    border: 1px solid var(--v2-primary);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary);
    color: var(--v2-primary-contrast, #fff);
    font-family: inherit;
    font-size: 0.88rem;
    font-weight: var(--v2-font-weight-medium);
    line-height: 1.2;
    cursor: pointer;
    box-sizing: border-box;
    transition: background var(--v2-transition-fast), border-color var(--v2-transition-fast);
}

.fts-next-btn:hover {
    background: var(--v2-primary-hover);
    border-color: var(--v2-primary-hover);
}

.fts-next-btn i {
    color: inherit !important;
    font-size: 0.8rem;
}

@media (max-width: 900px) {
    .fts-v2 {
        padding: 16px 18px 32px;
    }

    .fts-hero-title {
        font-size: 1.375rem;
    }
}
</style>
